
import { useState } from 'react';
import { toast } from 'sonner';
import { User } from '@/types';
import { userApi } from '../api/userApi';

export function useUserOperations() {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const formattedUsers = await userApi.fetchUsers();
      setUsers(formattedUsers);
      console.log('Users fetched and updated:', formattedUsers);
    } catch (error: any) {
      console.error('Error in fetchUsers:', error);
      toast.error(`Erreur lors du chargement des utilisateurs: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (user: User) => {
    try {
      await userApi.deleteUser(user.id);
      setUsers(users.filter(u => u.id !== user.id));
      toast.success(`Utilisateur ${user.firstName} ${user.lastName} supprimé avec succès`);
    } catch (error: any) {
      console.error('Error in handleDeleteUser:', error);
      toast.error(`Erreur lors de la suppression: ${error.message}`);
    }
  };

  const handleToggleUserStatus = async (userId: string) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) {
        toast.error("Utilisateur non trouvé");
        return;
      }

      const newStatus = !user.isActive;
      await userApi.toggleUserStatus(userId, newStatus);

      setUsers(users.map(u => 
        u.id === userId ? { ...u, isActive: newStatus } : u
      ));
      
      toast.success(`Utilisateur ${newStatus ? 'activé' : 'désactivé'} avec succès`);
    } catch (error: any) {
      console.error('Error in handleToggleUserStatus:', error);
      toast.error(`Erreur lors de la mise à jour du statut: ${error.message}`);
    }
  };

  const handleToggleUserRole = async (userId: string) => {
    try {
      const user = users.find(u => u.id === userId);
      if (!user) {
        toast.error("Utilisateur non trouvé");
        return;
      }

      const newRole = user.role === 'admin' ? 'user' : 'admin';
      await userApi.toggleUserRole(userId, newRole);

      // Update local state immediately for better UX
      setUsers(users.map(u => 
        u.id === userId ? { ...u, role: newRole as 'user' | 'admin' } : u
      ));
      
      console.log(`Role updated locally for user ${userId}: ${newRole}`);
      toast.success(`Rôle mis à jour: ${newRole === 'admin' ? 'Administrateur' : 'Utilisateur'}`);
      
      // Force refresh to ensure data consistency
      setTimeout(() => {
        fetchUsers();
      }, 500);
    } catch (error: any) {
      console.error('Error in handleToggleUserRole:', error);
      toast.error(`Erreur lors de la mise à jour du rôle: ${error.message}`);
      // Revert local state on error
      fetchUsers();
    }
  };

  const handleAddUser = async (newUser: Omit<User, 'id' | 'createdAt'>) => {
    try {
      const userExists = await userApi.checkUserExists(newUser.email);
      
      if (userExists) {
        toast.error("Un utilisateur avec cette adresse e-mail existe déjà");
        return Promise.reject(new Error("Email already exists"));
      }
      
      await userApi.addUser(newUser);
      await fetchUsers();
      toast.success(`Utilisateur ${newUser.firstName} ${newUser.lastName} ajouté avec succès`);
      return Promise.resolve();
    } catch (error: any) {
      const errorMessage = error.message?.includes("No authenticated user found") 
        ? "L'utilisateur doit d'abord créer un compte avec cette adresse e-mail"
        : `Erreur lors de l'ajout de l'utilisateur: ${error.message}`;
      
      console.error('Error in handleAddUser:', error);
      toast.error(errorMessage);
      return Promise.reject(error);
    }
  };

  return {
    users,
    isLoading,
    fetchUsers,
    handleDeleteUser,
    handleToggleUserStatus,
    handleToggleUserRole,
    handleAddUser
  };
}
